import { useMemo } from 'react';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { getDimensionData } from '@api/dimensionContainer';
import instance from '@api/axiosInstance';
import { trackQueryError } from '@utils/sensor/apptracker';
import useQueryParams from '@src/components/widget-panel/hooks/useQueryParams';
import useWidgetQuery from '@src/components/widget-panel/hooks/useWidgetQuery';
import { buildGroupByQuery } from '../network-widget/hooks/utils';

function useEventGroupBy({ wid, enabled = true }: any) {
  const { interval, filter, _rKey, liveMode } = useQueryParams();
  const EVENT_DIMENSION = 'event_name';
  const metric = 'AppTotalEvents';

  const groupByQuery = useMemo(() => {
    const q = buildGroupByQuery({
      dimension: EVENT_DIMENSION,
      metrics: [metric],
      filter,
      interval: `${moment(interval.from).toISOString()}/${moment(interval.to).toISOString()}`,
      limit: 200,
    }) as any;
    return q;
  }, [metric, filter, interval]);

  const {
    data = {},
    refetch,
    isFetching,
  } = useWidgetQuery({
    wid,
    queryKey: ['widget', 'event_metric_group', groupByQuery],
    queryFn: () => getDimensionData({ queries: [groupByQuery] }, {}, instance),
    options: {
      enabled: enabled && !liveMode,
      refetchOnWindowFocus: false,
      select: (response) => {
        const { data = [], totals = {} } = get(response, ['data', 0], {});
        return { data, totals };
      },
      retry: false,
      staleTime: 5 * 60 * 1000, // 5m
      onError: (err) => {
        trackQueryError({ type: 'event_metric_group', err });
      },
    },
  });

  const { eventList = [], validEvents = [] } = useMemo(() => {
    const eventRows = get(data, ['data', '0', 'results'], []);
    if (eventRows.length > 0) {
      const validEvents = eventRows.filter(
        (eachCode) => !['UNKNOWN'].includes(eachCode[EVENT_DIMENSION]) && !isNaN(eachCode[metric]),
      );
      const eventList = validEvents.map((code) => {
        return code[EVENT_DIMENSION];
      });
      return { eventList, validEvents };
    }
    return {};
  }, [data, metric]);

  return {
    eventList,
    validEvents,
    isLoading: isFetching,
    refetch,
  };
}

export default useEventGroupBy;
